import SuperFetch from 'library/helpers/superFetch';
import { put, takeEvery } from 'redux-saga/effects';
import { Notification } from 'zComponents/atoms';
import { inventoryActions } from './slice';

/**
 * The staff Inventory reads, one worker for all four (integration × type) datasets.
 *
 * This module is the ONLY place the inventory surface is named. Both vendors answer the
 * same question — "what hardware exists and who holds it" — through the same shape: a
 * vendor path segment plus a `type` query param.
 *
 * 🛑 `/Control` is not `/gateways`. That surface asks the vendor what THIS org's credentials
 * can see right now: no `organisation` join, no pagination, no `ip`/`ipConfigFlag`/`createDate`.
 * Pointing a tab there blanks half its columns and hides the unassigned pool it claims from.
 */
// The staff grids page client-side, so ask for the whole pool in one read.
const PAGE_SIZE = 2000;

const ENDPOINTS = {
	// `/TTlock` spells its lock type with the vendor's own name; `/Control` says what the row
	// IS, because the vendor is already the path segment. The difference stops here.
	//
	// 🛑 `paged` is not cosmetic. `/TTlock` validates a CLOSED envelope — its schema lists only
	// `type` and `unassigned`, and Joi rejects unknown keys — so sending page/size there is a
	// 400, not an ignored param. `/Control` requires them.
	control: { path: '/inventories/Control', type: { lock: 'lock', gateway: 'gateway' }, paged: true },
	ttlock: { path: '/inventories/TTlock', type: { lock: 'ttlock', gateway: 'gateway' }, paged: false }
};

/** The server's error envelope: `error` first and as a string, `message` is generic HTTP text. */
const apiError = (res, fallback) =>
	res?.error?.message ||
	(typeof res?.error === 'string' && res.error) ||
	(Array.isArray(res?.error) && res.error.join(', ')) ||
	res?.errors ||
	res?.info ||
	fallback;

/**
 * Load one dataset. Action payload: { integration, type }.
 *
 * An empty pool is a legitimate answer, so `[]` is success — the previous Control fetch
 * treated a non-200 as fatal and left the grid spinning forever, which an empty page used
 * to trigger through the old 404-on-empty route.
 */
export function* loadInventoryData(action) {
	const { integration, type } = action.payload || {};
	const endpoint = ENDPOINTS[integration];
	const wireType = endpoint?.type?.[type];

	if (!wireType) {
		yield put(inventoryActions.loadInventoryError({ integration, type, error: 'Unknown inventory dataset.' }));
		return;
	}

	try {
		const query = endpoint.paged ? { type: wireType, page: 1, size: PAGE_SIZE } : { type: wireType };
		const res = yield SuperFetch.get(endpoint.path, {}, query);
		if (res?.statusCode !== 200) throw new Error(apiError(res, 'Failed to load inventory.'));
		yield put(inventoryActions.loadInventorySuccess({ integration, type, rows: res.data || [] }));
	} catch (error) {
		const message = error.message || 'Failed to load inventory.';
		yield put(inventoryActions.loadInventoryError({ integration, type, error: message }));
		Notification('error', message);
	}
}

export default function* inventorySaga() {
	yield takeEvery(inventoryActions.loadInventory, loadInventoryData);
}
